import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowsClockwiseIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { formatRelativeTime } from '@budget/core';
import { apiFetch } from '../auth/apiFetch';
import { lock } from '../auth/PasswordGate';
import { useCurrentUser } from '../auth/useCurrentUser';
import { useFamily } from '../auth/useFamily';
import { ConnectBankButton } from '../components/ConnectBankButton';
import { IconButton } from '../components/IconButton';
import { Modal, ModalFooter } from '../components/Modal';
import { Tooltip } from '../components/Tooltip';
import { ThirdPartyLogo, hasLogo } from '../components/ThirdPartyLogos';
import { isAdmin } from '../config/admin';
import { usePlaidItems, type ManualImport, type PlaidItem } from '../data/usePlaidItems';
import { useRules } from '../data/useRules';
import { useTransactionsEnabled } from '../data/useTransactionsEnabled';
import { SettingsKiosksSection } from './SettingsKiosksSection';
import clsx from 'clsx';

function ChevronRightIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      width={18}
      height={18}
      className="shrink-0 ml-3 text-fg-tertiary"
    >
      <path
        d="M9 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatSyncedAt(iso: string): string {
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  return diffSec < 60 ? 'now' : formatRelativeTime(iso);
}

function stripCsvExt(name: string | null): string | null {
  if (!name) return name;
  return name.replace(/\.csv$/i, '');
}

// Parse a Postgres DATE value into a local-time Date. Neon returns DATE
// columns as either bare 'YYYY-MM-DD' or 'YYYY-MM-DDT00:00:00.000Z' depending
// on connection settings; both forms decode the same way after we slice off
// any trailing time portion. Returns null if the head doesn't look like ISO.
function parseIsoDate(iso: string): Date | null {
  const head = iso.slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(head);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateRange(startIso: string, endIso: string): string {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end) return `${startIso} – ${endIso}`;
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonthYear = sameYear && start.getMonth() === end.getMonth();
  const sameDay = sameMonthYear && start.getDate() === end.getDate();
  const monthDay = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const monthDayYear = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (sameDay) return monthDayYear(start);
  if (sameMonthYear) return `${monthDay(start)}–${end.getDate()}, ${end.getFullYear()}`;
  if (sameYear) return `${monthDay(start)} – ${monthDay(end)}, ${end.getFullYear()}`;
  return `${monthDayYear(start)} – ${monthDayYear(end)}`;
}

export function SettingsPage() {
  const me = useCurrentUser();
  const family = useFamily();
  const plaid = usePlaidItems();
  const rules = useRules();
  const { enabled: transactionsEnabled, setEnabled: setTransactionsEnabled } =
    useTransactionsEnabled();
  const adminViewer = me.status === 'ready' && me.mode === 'real' && isAdmin(me.user.email);
  const [syncingItemId, setSyncingItemId] = useState<number | null>(null);
  const [syncMessage, setSyncMessage] = useState<{ itemId: number; text: string } | null>(null);
  const [disconnectingItemId, setDisconnectingItemId] = useState<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [importingItemId, setImportingItemId] = useState<number | null>(null);
  const [importMessage, setImportMessage] = useState<{ itemId: number | null; text: string } | null>(null);
  const [removingImportId, setRemovingImportId] = useState<number | null>(null);
  // One hidden file input shared across all import affordances; the click
  // handler scopes which item the upload should land under via state.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingImportItemId, setPendingImportItemId] = useState<number | null>(null);

  // Manual Apple Card item for the current user, if one exists. Drives
  // whether we render the bottom "Import Apple Card statement…" button
  // (creates the first item) vs. the per-item "Import statement…" button.
  const myAppleCardItem = useMemo(() => {
    if (plaid.status !== 'ready') return null;
    return plaid.items.find((i) => i.source === 'manual') ?? null;
  }, [plaid]);

  // Apple Card import is admin-only for now (Dakota). Non-admins should
  // never see import affordances or any manual items in their list, even
  // defensively if a manual item somehow ended up associated with them.
  const visibleItems = useMemo(() => {
    if (plaid.status !== 'ready') return [];
    return adminViewer
      ? plaid.items
      : plaid.items.filter((i) => i.source !== 'manual');
  }, [plaid, adminViewer]);

  // The modal reads the current item state out of the live plaid hook each
  // render, so removals reflect immediately without prop drilling stale data.
  const editingItem = useMemo(() => {
    if (editingItemId == null || plaid.status !== 'ready') return null;
    return plaid.items.find((i) => i.id === editingItemId) ?? null;
  }, [editingItemId, plaid]);

  // Auto-close the modal if its item disappears (disconnected entirely).
  useEffect(() => {
    if (
      editingItemId != null &&
      plaid.status === 'ready' &&
      !plaid.items.some((i) => i.id === editingItemId)
    ) {
      setEditingItemId(null);
    }
  }, [editingItemId, plaid]);

  // A 404 with a JSON body comes from our endpoint and means the row is
  // already gone server-side — treat as success and reload. A 404 without
  // a JSON body is Vercel's route-not-found fallback (the endpoint isn't
  // deployed yet); surface it as an error so the user can act on it.
  const disconnect = async (
    payload: { itemId?: number; accountId?: number },
  ): Promise<{ ok: boolean; error?: string }> => {
    const res = await apiFetch('/api/plaid/disconnect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const isJsonResponse = (res.headers.get('Content-Type') ?? '').includes(
      'application/json',
    );
    if (res.ok || (res.status === 404 && isJsonResponse)) {
      if (plaid.status === 'ready') plaid.reload();
      return { ok: true };
    }
    const body = await res.text().catch(() => '');
    const detail = body.slice(0, 200) || '(no response body)';
    return { ok: false, error: `disconnect ${res.status}: ${detail}` };
  };

  const disconnectItem = async (itemId: number, institutionName: string | null) => {
    const label = institutionName ?? 'this bank';
    const confirmed = window.confirm(
      `Disconnect ${label}?\n\nYou'll lose all synced transactions for every account at this bank, along with any categorizations you've made on them. You can reconnect later, but the categorizations won't come back.`,
    );
    if (!confirmed) return;
    setDisconnectingItemId(itemId);
    setSyncMessage(null);
    setEditError(null);
    try {
      const result = await disconnect({ itemId });
      if (!result.ok) {
        const text = result.error ?? 'disconnect failed';
        setSyncMessage({ itemId, text });
        setEditError(text);
      }
    } finally {
      setDisconnectingItemId(null);
    }
  };

  const disconnectAccount = async (
    itemId: number,
    accountId: number,
    accountLabel: string,
  ) => {
    const confirmed = window.confirm(
      `Remove ${accountLabel}?\n\nYou'll lose all synced transactions for this account, along with any categorizations you've made on them. You can re-link the bank to restore it, but the categorizations won't come back.`,
    );
    if (!confirmed) return;
    setDisconnectingItemId(itemId);
    setSyncMessage(null);
    setEditError(null);
    try {
      const result = await disconnect({ accountId });
      if (!result.ok) {
        const text = result.error ?? 'remove failed';
        setSyncMessage({ itemId, text });
        setEditError(text);
      }
    } finally {
      setDisconnectingItemId(null);
    }
  };

  const syncItem = async (itemId: number) => {
    setSyncingItemId(itemId);
    setSyncMessage(null);
    try {
      const res = await apiFetch('/api/plaid/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`sync ${res.status}: ${body.slice(0, 200)}`);
      }
      await res.json();
      if (plaid.status === 'ready') plaid.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'sync failed';
      setSyncMessage({ itemId, text: msg });
    } finally {
      setSyncingItemId(null);
    }
  };

  // Open the OS file picker scoped to a target item id. The actual upload
  // happens in onFileChange below once the user selects a file.
  const triggerImport = (itemId: number | null) => {
    setPendingImportItemId(itemId);
    setImportMessage(null);
    fileInputRef.current?.click();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    // Reset so selecting the same filename twice still fires onChange.
    if (e.target) e.target.value = '';
    if (!file) return;
    const targetItemId = pendingImportItemId;
    setPendingImportItemId(null);
    setImportingItemId(targetItemId ?? -1);
    try {
      const text = await file.text();
      const qs = file.name ? `?filename=${encodeURIComponent(file.name)}` : '';
      const res = await apiFetch(`/api/transactions/import-applecard${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        body: text,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`import ${res.status}: ${body.slice(0, 200)}`);
      }
      const result = (await res.json()) as {
        inserted: number;
        merged: number;
        removed: number;
        rowCount: number;
      };
      const parts = [`Imported ${result.inserted} new`];
      if (result.merged > 0) {
        parts.push(
          `${result.merged} merged from earlier import${result.merged === 1 ? '' : 's'}`,
        );
      }
      if (result.removed > 0) {
        parts.push(
          `removed ${result.removed} empty import${result.removed === 1 ? '' : 's'}`,
        );
      }
      setImportMessage({
        itemId: targetItemId,
        text: parts.join(' · '),
      });
      if (plaid.status === 'ready') plaid.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'import failed';
      setImportMessage({ itemId: targetItemId, text: msg });
    } finally {
      setImportingItemId(null);
    }
  };

  const removeImport = async (importId: number, label: string) => {
    const confirmed = window.confirm(
      `Remove ${label}?\n\nThe transactions imported in this batch will be deleted. Re-uploading the same CSV will re-insert them.`,
    );
    if (!confirmed) return;
    setRemovingImportId(importId);
    setEditError(null);
    try {
      const res = await apiFetch(
        `/api/transactions/import-applecard?importId=${importId}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        const text = `remove ${res.status}: ${body.slice(0, 200)}`;
        setEditError(text);
        return;
      }
      if (plaid.status === 'ready') plaid.reload();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'remove failed');
    } finally {
      setRemovingImportId(null);
    }
  };

  const handleLogout = () => {
    lock();
    window.location.reload();
  };

  return (
    <div className="page-container-narrow space-y-6">
      <header>
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your data and session</p>
      </header>

      <section className="settings-section">
        <h3 className="section-title">Profile</h3>
        <div className="card-flush">
          <div className="settings-row">
            {me.status === 'ready' && me.mode === 'real' && me.user ? (
              <>
                {me.user.pictureUrl ? (
                  <img
                    src={me.user.pictureUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="size-12 rounded-full border border-border"
                  />
                ) : (
                  <div className="size-12 rounded-full border border-border bg-subtle flex items-center justify-center text-base font-medium text-fg-secondary">
                    {(me.user.displayName ?? me.user.email).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 ml-4 min-w-0">
                  <div className="font-medium truncate">
                    {me.user.displayName ?? me.user.email}
                  </div>
                  <div className="text-sm text-fg-secondary truncate">
                    {me.user.email}
                  </div>
                </div>
              </>
            ) : me.status === 'ready' && me.mode === 'demo' ? (
              <div className="flex-1 min-w-0">
                <div className="font-medium">Demo session</div>
                <div className="text-sm text-fg-secondary">
                  Values are scrambled and names are swapped.
                </div>
              </div>
            ) : me.status === 'ready' && me.mode === 'real-legacy' ? (
              <div className="flex-1 min-w-0">
                <div className="font-medium">Password session</div>
                <div className="text-sm text-fg-secondary">
                  Sign in with Google to see your profile.
                </div>
              </div>
            ) : me.status === 'error' ? (
              <div className="flex-1 text-sm text-red">{me.error}</div>
            ) : (
              <div className="flex-1 text-sm text-fg-secondary">Loading…</div>
            )}
            <button onClick={handleLogout} className="btn btn-outline ml-4 shrink-0">
              Logout
            </button>
          </div>
        </div>
      </section>

      {me.status === 'ready' && me.mode === 'real' && transactionsEnabled && (
        <section className="settings-section">
          <h3 className="section-title">Connected accounts</h3>
          <div className="card-flush">
            {plaid.status === 'loading' && (
              <div className="settings-row">
                <div className="text-sm text-fg-secondary">Loading…</div>
              </div>
            )}
            {plaid.status === 'error' && (
              <div className="settings-row">
                <div className="text-sm text-red">{plaid.error}</div>
              </div>
            )}
            {plaid.status === 'ready' && visibleItems.length === 0 && (
              <div className="settings-row">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">No banks connected</div>
                  <div className="text-sm text-fg-secondary">
                    Link an account to start tracking actual spending.
                  </div>
                </div>
                <ConnectBankButton onConnected={() => plaid.reload()} />
              </div>
            )}
            {plaid.status === 'ready' &&
              visibleItems.map((item) => {
                const isManual = item.source === 'manual';
                const displayName =
                  item.institutionName ?? (isManual ? 'Apple Card' : 'Unknown institution');
                // imports[] is sorted by statement date (newest period first),
                // so pull "last imported" from the actual max upload time.
                const lastImportedAt = isManual && item.imports.length > 0
                  ? item.imports.reduce(
                      (acc, i) => (i.importedAt > acc ? i.importedAt : acc),
                      item.imports[0].importedAt,
                    )
                  : null;
                const subtitle = isManual
                  ? lastImportedAt
                    ? `Last imported ${formatSyncedAt(lastImportedAt)} · ${item.imports.length} import${item.imports.length === 1 ? '' : 's'}`
                    : 'No imports yet'
                  : item.lastSyncedAt
                    ? `Synced ${formatSyncedAt(item.lastSyncedAt)}`
                    : 'Not synced';
                const showMsg =
                  syncMessage?.itemId === item.id || importMessage?.itemId === item.id;
                const msgText =
                  syncMessage?.itemId === item.id
                    ? syncMessage.text
                    : importMessage?.itemId === item.id
                      ? importMessage.text
                      : '';
                return (
                  <div key={item.id} className="border-b border-border">
                    <div className="flex items-center px-4 py-3 sm:px-5 sm:py-4">
                      <div className="shrink-0 mr-3 flex items-center justify-center">
                        <ThirdPartyLogo location={displayName} size={28} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{displayName}</div>
                        <div className="text-sm text-fg-secondary">{subtitle}</div>
                        {showMsg && (
                          <div className="text-xs text-fg-secondary mt-1">{msgText}</div>
                        )}
                      </div>
                      {isManual ? (
                        <button
                          type="button"
                          onClick={() => triggerImport(item.id)}
                          disabled={
                            importingItemId !== null || disconnectingItemId !== null
                          }
                          className="btn btn-outline ml-3 shrink-0"
                        >
                          {importingItemId === item.id ? 'Importing…' : 'Import CSV'}
                        </button>
                      ) : (
                        // Manual "Sync now" is admin-only — non-admins rely on
                        // the daily cron sync. Avoids duplicating Plaid API
                        // calls (and billing) when both household members hit
                        // the button independently.
                        adminViewer && (
                          <Tooltip content={syncingItemId === item.id ? 'Syncing…' : 'Sync now'}>
                            <IconButton
                              onClick={() => void syncItem(item.id)}
                              disabled={
                                syncingItemId !== null || disconnectingItemId !== null
                              }
                              aria-label={syncingItemId === item.id ? 'Syncing…' : 'Sync now'}
                              size="lg"
                              className="ml-3"
                              icon={
                                <ArrowsClockwiseIcon
                                  size={18}
                                  weight="bold"
                                  className={clsx(syncingItemId === item.id && 'animate-spin')}
                                />
                              }
                            />
                          </Tooltip>
                        )
                      )}
                      <Tooltip content="Edit">
                        <IconButton
                          onClick={() => setEditingItemId(item.id)}
                          disabled={
                            syncingItemId !== null ||
                            disconnectingItemId !== null ||
                            importingItemId !== null
                          }
                          aria-label={`Edit ${displayName}`}
                          size="lg"
                          className="ml-2"
                          icon={<PencilSimpleIcon size={16} weight="bold" />}
                        />
                      </Tooltip>
                    </div>
                    {isManual ? (
                      item.imports.length > 0 && (
                        <>
                          <div className="ml-[3.75rem] h-px bg-border" />
                          {item.imports.map((imp, iidx, iarr) => (
                            <Fragment key={imp.id}>
                              <div className="flex items-center justify-between pl-[3.75rem] pr-5 py-3">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">
                                    {stripCsvExt(imp.filename) ?? `Import #${imp.id}`}
                                  </div>
                                  <div className="text-xs text-fg-tertiary">
                                    {imp.currentCount} transaction
                                    {imp.currentCount === 1 ? '' : 's'}
                                    {imp.earliestDate && imp.latestDate
                                      ? ` · ${formatDateRange(imp.earliestDate, imp.latestDate)}`
                                      : ''}
                                  </div>
                                </div>
                              </div>
                              {iidx < iarr.length - 1 && (
                                <div className="ml-[3.75rem] h-px bg-border" />
                              )}
                            </Fragment>
                          ))}
                        </>
                      )
                    ) : (
                      <>
                        {item.accounts.length > 0 && (
                          <div className="ml-[3.75rem] h-px bg-border" />
                        )}
                        {item.accounts.map((a, aidx, aarr) => {
                          const accountLabel = `${a.name}${a.mask ? ` ••${a.mask}` : ''}`;
                          return (
                            <Fragment key={a.id}>
                              <Link
                                to={`/spending?tab=transactions&account=${a.id}`}
                                className="flex items-center justify-between pl-[3.75rem] pr-5 py-3 hover:bg-subtle transition-colors"
                              >
                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                  <span className="text-sm font-medium truncate">
                                    {accountLabel}
                                  </span>
                                </div>
                                <ChevronRightIcon />
                              </Link>
                              {aidx < aarr.length - 1 && (
                                <div className="ml-[3.75rem] h-px bg-border" />
                              )}
                            </Fragment>
                          );
                        })}
                      </>
                    )}
                  </div>
                );
              })}
            {plaid.status === 'ready' && visibleItems.length > 0 && (
              <div className="settings-row">
                <div className="flex-1" />
                {adminViewer && !myAppleCardItem && (
                  <button
                    type="button"
                    onClick={() => triggerImport(null)}
                    disabled={importingItemId !== null}
                    className="btn btn-outline mr-2"
                  >
                    {importingItemId === -1 ? 'Importing…' : 'Import Apple Card CSV'}
                  </button>
                )}
                <ConnectBankButton onConnected={() => plaid.reload()}>
                  Connect another bank
                </ConnectBankButton>
              </div>
            )}
            {plaid.status === 'ready' &&
              visibleItems.length === 0 &&
              adminViewer &&
              !myAppleCardItem && (
                <div className="settings-row border-t border-border">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">Import Apple Card</div>
                    <div className="text-sm text-fg-secondary">
                      Upload a CSV statement to back-fill transactions.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => triggerImport(null)}
                    disabled={importingItemId !== null}
                    className="btn btn-outline ml-4 shrink-0"
                  >
                    {importingItemId === -1 ? 'Importing…' : 'Choose CSV…'}
                  </button>
                </div>
              )}
            {adminViewer && importMessage?.itemId === null && importMessage.text && (
              <div className="px-4 sm:px-5 py-2 text-xs text-fg-secondary border-t border-border">
                {importMessage.text}
              </div>
            )}
          </div>
          {adminViewer && (
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onFileChange}
            />
          )}
          {plaid.status === 'ready' &&
            me.status === 'ready' &&
            me.user?.email === 'dakotaberg@gmail.com' &&
            (() => {
              const missing = Array.from(
                new Set(plaid.householdInstitutionNames.filter((n) => !hasLogo(n))),
              );
              if (missing.length === 0) return null;
              return (
                <div className="mt-2 px-3 py-2 rounded-[var(--radius-sm)] border border-dashed border-border-strong text-xs text-fg-secondary">
                  <span className="font-medium text-fg">Missing brand logo</span>
                  {missing.length === 1 ? ' for ' : 's for '}
                  {missing.map((name, i) => (
                    <span key={name}>
                      {i > 0 && ', '}
                      <span className="font-medium text-fg">{name}</span>
                    </span>
                  ))}
                  . Add an SVG to <span className="tabular">ThirdPartyLogos.tsx</span>.
                </div>
              );
            })()}
        </section>
      )}

      {me.status === 'ready' && me.mode === 'real' && (
        <section className="settings-section">
          <h3 className="section-title">Family</h3>
          <div className="card-flush">
            {family.status === 'loading' && (
              <div className="settings-row">
                <div className="text-sm text-fg-secondary">Loading…</div>
              </div>
            )}
            {family.status === 'error' && (
              <div className="settings-row">
                <div className="text-sm text-red">{family.error}</div>
              </div>
            )}
            {family.status === 'ready' && family.members.length === 0 && (
              <div className="settings-row">
                <div className="text-sm text-fg-secondary">
                  No one else has signed in yet.
                </div>
              </div>
            )}
            {family.status === 'ready' &&
              family.members.map((m) => (
                <div className="settings-row" key={m.email}>
                  {m.pictureUrl ? (
                    <img
                      src={m.pictureUrl}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="size-12 rounded-full border border-border"
                    />
                  ) : (
                    <div className="size-12 rounded-full border border-border bg-subtle flex items-center justify-center text-base font-medium text-fg-secondary">
                      {(m.displayName ?? m.email).slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 ml-4 min-w-0">
                    <div className="font-medium truncate">{m.displayName ?? m.email}</div>
                    <div className="text-sm text-fg-secondary truncate">
                      {m.email}
                    </div>
                  </div>
                  <div className="text-sm text-fg-secondary tabular shrink-0 ml-4">
                    Last active {formatRelativeTime(m.lastSeenAt)}
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {me.status === 'ready' && me.mode === 'real' && (
        <SettingsKiosksSection adminViewer={adminViewer} />
      )}

      {me.status === 'ready' &&
        me.mode === 'real' &&
        transactionsEnabled &&
        rules.status === 'ready' &&
        rules.rules.length > 0 && (
          <section className="settings-section">
            <h3 className="section-title">More</h3>
            <div className="card-flush">
              <Link
                to="/settings/rules"
                className="settings-row hover:bg-overlay transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Categorization rules</div>
                  <div className="text-sm text-fg-secondary">
                    Auto-apply categories to matching transactions during sync.
                  </div>
                </div>
                <span className="text-sm text-fg-tertiary tabular shrink-0 ml-4">
                  {`${rules.rules.length} rule${rules.rules.length === 1 ? '' : 's'}`}
                </span>
                <ChevronRightIcon />
              </Link>
            </div>
          </section>
        )}

      {adminViewer && (
        <section className="settings-section">
          <h3 className="section-title">Admin</h3>
          <div className="card-flush">
            <div className="settings-row">
              <div className="flex-1 min-w-0">
                <div className="font-medium">Transactions</div>
                <div className="text-sm text-fg-secondary">
                  Enables transaction tracking in the app.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTransactionsEnabled(!transactionsEnabled)}
                className="btn btn-outline ml-4 shrink-0"
              >
                {transactionsEnabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        </section>
      )}

      {editingItem && (
        <EditItemModal
          item={editingItem}
          busy={disconnectingItemId !== null || removingImportId !== null}
          error={editError}
          onClose={() => {
            setEditingItemId(null);
            setEditError(null);
          }}
          onDisconnectItem={() =>
            void disconnectItem(
              editingItem.id,
              editingItem.institutionName ??
                (editingItem.source === 'manual' ? 'Apple Card' : null),
            )
          }
          onDisconnectAccount={(accountId, label) =>
            void disconnectAccount(editingItem.id, accountId, label)
          }
          onRemoveImport={(importId, label) => void removeImport(importId, label)}
        />
      )}
    </div>
  );
}

interface EditItemModalProps {
  item: PlaidItem;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onDisconnectItem: () => void;
  onDisconnectAccount: (accountId: number, accountLabel: string) => void;
  onRemoveImport: (importId: number, label: string) => void;
}

function importLabel(imp: ManualImport): string {
  return stripCsvExt(imp.filename) ?? `Import #${imp.id}`;
}

function EditItemModal({
  item,
  busy,
  error,
  onClose,
  onDisconnectItem,
  onDisconnectAccount,
  onRemoveImport,
}: EditItemModalProps) {
  const isManual = item.source === 'manual';
  const displayName =
    item.institutionName ?? (isManual ? 'Apple Card' : 'Bank');
  const subtitle = isManual
    ? `${item.imports.length} import${item.imports.length === 1 ? '' : 's'}`
    : `${item.accounts.length} account${item.accounts.length === 1 ? '' : 's'}`;

  return (
    <Modal onClose={onClose}>
      <div className="flex items-start gap-3 p-6 pb-4">
        <span className="shrink-0 mt-0.5">
          <ThirdPartyLogo location={displayName} size={32} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-lg font-semibold truncate">{displayName}</div>
          <div className="text-sm text-fg-tertiary">{subtitle}</div>
        </div>
      </div>

      <div className="border-t border-border">
        {isManual ? (
          item.imports.length === 0 ? (
            <div className="px-6 py-5 text-sm text-fg-secondary">
              No imports yet.
            </div>
          ) : (
            item.imports.map((imp, idx) => (
              <div
                key={imp.id}
                className={`flex items-center px-6 py-3 ${
                  idx > 0 ? 'border-t border-border' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {importLabel(imp)}
                  </div>
                  <div className="text-xs text-fg-tertiary">
                    {imp.currentCount} transaction
                    {imp.currentCount === 1 ? '' : 's'}
                    {imp.earliestDate && imp.latestDate
                      ? ` · ${formatDateRange(imp.earliestDate, imp.latestDate)}`
                      : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveImport(imp.id, importLabel(imp))}
                  disabled={busy}
                  className="text-sm text-fg-tertiary hover:text-red transition-colors disabled:opacity-40 disabled:hover:text-fg-tertiary"
                >
                  Remove
                </button>
              </div>
            ))
          )
        ) : item.accounts.length === 0 ? (
          <div className="px-6 py-5 text-sm text-fg-secondary">
            No accounts left. Disconnect the bank below to remove it entirely.
          </div>
        ) : (
          item.accounts.map((a, idx) => {
            const accountLabel = `${a.name}${a.mask ? ` ••${a.mask}` : ''}`;
            return (
              <div
                key={a.id}
                className={`flex items-center px-6 py-3 ${
                  idx > 0 ? 'border-t border-border' : ''
                }`}
              >
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {accountLabel}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onDisconnectAccount(a.id, accountLabel)}
                  disabled={busy}
                  className="text-sm text-fg-tertiary hover:text-red transition-colors disabled:opacity-40 disabled:hover:text-fg-tertiary"
                >
                  Remove
                </button>
              </div>
            );
          })
        )}
      </div>

      {error && (
        <div className="border-t border-border px-4 py-3 text-sm text-red break-words">
          {error}
        </div>
      )}

      <ModalFooter align="between">
        <button
          type="button"
          onClick={onDisconnectItem}
          disabled={busy}
          className="btn btn-danger-outline"
        >
          {isManual ? 'Disconnect Apple Card' : 'Disconnect bank'}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="btn btn-outline"
        >
          Done
        </button>
      </ModalFooter>
    </Modal>
  );
}
